import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Package,
  Zap,
  BarChart3,
  Receipt,
  Crown,
  ArrowRight,
  AlertCircle,
  Calendar,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { billingApi } from "@/services/api";
import { formatDate } from "@/utils/dateFormat";

export default function BillingOverview() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // ============================================
  // TanStack Query - Fetch Subscription
  // ============================================
  const {
    data: subscription,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["billing-subscription"],
    queryFn: async () => {
      const response = await billingApi.getSubscription();
      return response.data.data;
    },
  });

  // ============================================
  // TanStack Query - Resume Subscription Mutation
  // ============================================
  const resumeSubscriptionMutation = useMutation({
    mutationFn: () => billingApi.resumeSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
    },
  });

  useEffect(() => {
    // Check for success/canceled from Stripe redirect
    if (searchParams.get("success") === "true") {
      toast.success("Payment successful! Your subscription has been updated.");
    } else if (searchParams.get("canceled") === "true") {
      toast.error("Payment was canceled.");
    }
  }, [searchParams]);

  const handleManageBilling = async () => {
    try {
      const response = await billingApi.createPortalSession();
      window.location.href = response.data.data.url;
    } catch {
      toast.error("Failed to open billing portal");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error?.response?.data?.error || "Failed to load billing information"}
        </AlertDescription>
      </Alert>
    );
  }

  const {
    plan,
    planDetails,
    status,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    addons,
    credits,
    limitsAndUsage,
  } = subscription || {};

  // Helper to determine if plan is a paid/premium plan
  const isPaidPlan = plan && plan !== "free";
  // "Enterprise" here means a negotiated, sales-managed plan. `custom` is the
  // plan id that replaced `enterprise`; an invoice-billed org has no Stripe
  // subscription, so it must get "Contact Account Manager" rather than a
  // Stripe billing-portal button that would only error.
  const isEnterprise =
    plan === "enterprise" ||
    plan === "custom" ||
    subscription?.billingMode === "invoice";

  // Get display name for plan
  const getPlanDisplayName = () => {
    if (planDetails?.name) return planDetails.name;
    if (isEnterprise) return "Enterprise";
    if (plan) return plan.charAt(0).toUpperCase() + plan.slice(1);
    return t("billing.freePlan");
  };

  // Render price display based on plan type
  const renderPriceDisplay = () => {
    // A negotiated plan usually has an agreed price (custom_plan_price); show
    // it. Only fall back to "Custom" when none has been set yet, which is what
    // an enterprise org with no configured price looks like.
    if (isEnterprise) {
      // Pay-as-you-go has no recurring fee at all; saying "Custom" here would
      // imply an agreed amount the customer never sees on an invoice.
      if (subscription?.customBillingType === "payg") {
        return (
          <div className="text-2xl font-bold text-primary">
            {t("billing.payAsYouGo", "Pay as you go")}
          </div>
        );
      }
      const negotiated =
        planDetails?.priceMonthlyDisplay || planDetails?.priceYearlyDisplay;
      if (negotiated > 0) {
        return (
          <>
            <div className="text-3xl font-bold">${negotiated}</div>
            <div className="text-sm text-muted-foreground">
              {planDetails?.priceYearlyDisplay > 0
                ? t("billing.perYear", "per year")
                : t("billing.perMonth", "per month")}
            </div>
          </>
        );
      }
      return (
        <div className="text-2xl font-bold text-primary">
          {t("billing.customPricing", "Custom")}
        </div>
      );
    }

    // Paid plans with price
    if (planDetails?.priceMonthlyDisplay > 0) {
      return (
        <>
          <div className="text-3xl font-bold">
            ${planDetails.priceMonthlyDisplay}
          </div>
          <div className="text-sm text-muted-foreground">
            {t("billing.perUserMonth")}
          </div>
        </>
      );
    }

    // Free plan
    return (
      <div className="text-2xl font-bold text-muted-foreground">
        {t("billing.freePlan")}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("common.overview")}
          </h2>
          <p className="text-muted-foreground">
            {t("billing.subscriptionSummary")}
          </p>
        </div>
      </div>

      {/* Cancellation Warning */}
      {cancelAtPeriodEnd && (
        <Alert
          variant="warning"
          className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950"
        >
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
            Your subscription is set to cancel on{" "}
            {formatDate(currentPeriodEnd)}. You'll be
            downgraded to the Free plan after this date.
            <Button
              variant="link"
              className="ml-2 h-auto p-0"
              onClick={() => resumeSubscriptionMutation.mutate()}
            >
              Resume subscription
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Current Plan Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-sm font-medium">
              {t("billing.currentPlan")}
            </CardTitle>
            <CardDescription>
              {t("billing.overview.subscriptionDetails")}
            </CardDescription>
          </div>
          <Crown className="h-8 w-8 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold">
                  {getPlanDisplayName()}
                </span>
                {planDetails?.popular && (
                  <Badge variant="secondary">
                    {t("billing.plans.popular")}
                  </Badge>
                )}
                {isEnterprise && (
                  <Badge variant="default" className="bg-primary">
                    Enterprise
                  </Badge>
                )}
                <Badge variant={status === "active" ? "success" : "warning"}>
                  {status === "active" ? t("common.active") : status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {planDetails?.description ||
                  (isEnterprise &&
                    "Custom enterprise solution with dedicated support")}
              </p>
              {currentPeriodEnd && (
                <p className="text-sm text-muted-foreground mt-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  {cancelAtPeriodEnd
                    ? t("common.cancels")
                    : t("common.renews")}{" "}
                  on {formatDate(currentPeriodEnd)}
                </p>
              )}
            </div>
            <div className="text-right">{renderPriceDisplay()}</div>
          </div>
          <div className="mt-4 flex gap-2">
            {!isEnterprise && (
              <Button asChild>
                <Link to="/settings/billing/plans">
                  {plan === "free"
                    ? t("billing.plans.upgrade")
                    : t("billing.changePlan")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            )}
            {isEnterprise && (
              <Button asChild variant="outline">
                <a href="mailto:support@escribe.io">Contact Account Manager</a>
              </Button>
            )}
            {isPaidPlan && !isEnterprise && (
              <Button variant="outline" onClick={handleManageBilling}>
                {t("billing.manageSubscription")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Add-ons */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("billing.activeAddons")}
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{addons?.length || 0}</div>
            <Link
              to="/settings/billing/addons"
              className="text-sm text-primary hover:underline"
            >
              {t("billing.addons.configure")} →
            </Link>
          </CardContent>
        </Card>

        {/* SMS Credits */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("billing.smsCredits")}
            </CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {credits?.sms?.balance || 0}
            </div>
            <Link
              to="/settings/billing/credits?type=sms"
              className="text-sm text-primary hover:underline"
            >
              Purchase credits →
            </Link>
          </CardContent>
        </Card>

        {/* Envelopes Used */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("envelopes.used")}
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {limitsAndUsage?.envelopes ? (
              <>
                <div className="text-2xl font-bold">
                  {limitsAndUsage.envelopes.used}
                  {limitsAndUsage.envelopes.limit !== -1 && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{limitsAndUsage.envelopes.limit}
                    </span>
                  )}
                </div>
                {limitsAndUsage.envelopes.limit !== -1 && (
                  <Progress
                    value={limitsAndUsage.envelopes.percentUsed}
                    className="mt-2 h-2"
                  />
                )}
              </>
            ) : (
              <div className="text-2xl font-bold">0</div>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("billing.invoices.title")}
            </CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/billing/invoices">
                {t("billing.viewHistory")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Usage Overview */}
      <Card>
        <CardHeader>
          <CardTitle>{t("billing.usageThisPeriod")}</CardTitle>
          <CardDescription>{t("usage.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {limitsAndUsage &&
              Object.entries(limitsAndUsage).map(([key, data]) => (
                <UsageItem key={key} name={formatLimitName(key)} data={data} />
              ))}
          </div>
          <div className="mt-4">
            <Link
              to="/settings/billing/usage"
              className="text-sm text-primary hover:underline"
            >
              {t("billing.overview.viewUsage")} →
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Active Add-ons */}
      {addons && addons.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("billing.activeAddons")}</CardTitle>
            <CardDescription>
              {t("billing.addons.yourSubscription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {addons.map((addon) => (
                <div
                  key={addon.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div>
                    <div className="font-medium">
                      {addon.addonDetails?.tierName || addon.addonType}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      ${addon.addonDetails?.priceMonthly}/{t("common.month")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        addon.status === "active" ? "success" : "warning"
                      }
                    >
                      {addon.status}
                    </Badge>
                    {addon.cancelAtPeriodEnd && (
                      <Badge variant="warning">{t("common.canceling")}</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button variant="outline" asChild>
                <Link to="/settings/billing/addons">
                  {t("billing.addons.configure")}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="cursor-pointer hover:border-primary transition-colors"
          asChild
        >
          <Link to="/settings/billing/plans">
            <CardHeader>
              <Crown className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">
                {t("billing.plans.title")}
              </CardTitle>
              <CardDescription>{t("billing.comparePlans")}</CardDescription>
            </CardHeader>
          </Link>
        </Card>
        <Card
          className="cursor-pointer hover:border-primary transition-colors"
          asChild
        >
          <Link to="/settings/billing/addons">
            <CardHeader>
              <Package className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">
                {t("billing.addons.title")}
              </CardTitle>
              <CardDescription>
                {t("billing.addons.description")}
              </CardDescription>
            </CardHeader>
          </Link>
        </Card>
        <Card
          className="cursor-pointer hover:border-primary transition-colors"
          asChild
        >
          <Link to="/settings/billing/credits">
            <CardHeader>
              <Zap className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">
                {t("billing.credits.title")}
              </CardTitle>
              <CardDescription>{t("billing.purchaseCredits")}</CardDescription>
            </CardHeader>
          </Link>
        </Card>
      </div>
    </div>
  );
}

const UsageItem = ({ name, data }) => {
  if (!data) return null;

  const isUnlimited = data.limit === -1;
  const percentUsed = isUnlimited ? 0 : data.percentUsed || 0;
  const isWarning = percentUsed >= 80;
  const isDanger = percentUsed >= 95;

  const getUsageItemClass = (isNotUnlimited) => {
    if (isNotUnlimited) {
      if (isDanger) {
        return "[&>div]:bg-red-600";
      }
      return isWarning ? "[&>div]:bg-yellow-600" : "";
    }

    if (isDanger) {
      return "text-red-600";
    }
    return isWarning ? "text-yellow-600" : "text-muted-foreground";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{name}</span>
        <span className={getUsageItemClass()}>
          {data.used}
          {!isUnlimited && <span>/{data.limit}</span>}
          {isUnlimited && <span className="ml-1 text-xs">(unlimited)</span>}
        </span>
      </div>
      {!isUnlimited && (
        <Progress
          value={percentUsed}
          className={`h-2 ${getUsageItemClass(true)}`}
        />
      )}
    </div>
  );
};

const formatLimitName = (key) => {
  const names = {
    envelopes: "Envelopes",
    templates: "Templates",
    teamMembers: "Team Members",
    signingLinks: "Signing Links",
    clauses: "Clauses",
    folders: "Folders",
    tags: "Tags",
    apiCalls: "API Calls",
  };
  return names[key] || key;
};
